import { apiClient } from './api-client';
import type { AuthorizationCommunicationMethod } from './authorization-case.service';

/**
 * Phase 7 Slice 5B — the staff-plane client of the request-scoped quotation
 * thread (docs/AUTHORIZATION_REQUEST_SCOPED_QUOTATION_DESIGN.md §J; D-127 OD-5,
 * D-128 Q-1 … Q-4, Q-7).
 *
 * Every call maps 1:1 onto the deployed Slice 5A endpoints under
 * `/authorization-cases/:id/quotation`. Nothing here derives state of its own:
 * `threadStatus`, `closed` and `currentRevision` are computed by the server and
 * rendered as-is. Writes reuse the EXISTING
 * `inspection:authorization:record-communication` permission (Q-3); ownership
 * (COMMERCIAL, claim-first, assignee-only) is enforced server-side.
 *
 * This module is deliberately separate from `customer-quotation.service.ts`
 * (the job-level quotation on the Inspection) — the two subsystems share no
 * type, endpoint or state (D-127 §1, D-113 §9).
 */

/** Per-revision status — exactly the five values of the Slice 5A enum (Q-7). */
export type AuthorizationQuotationRevisionStatus =
  'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'MODIFICATION_REQUESTED';

/** Derived thread state — `NONE` before revision 1 exists. */
export type AuthorizationQuotationThreadStatus = 'NONE' | AuthorizationQuotationRevisionStatus;

export const QUOTATION_REVISION_STATUS_LABELS: Record<
  AuthorizationQuotationRevisionStatus,
  string
> = {
  DRAFT: 'Draft',
  SENT: 'Sent — awaiting customer',
  ACCEPTED: 'Accepted by customer',
  REJECTED: 'Rejected by customer',
  MODIFICATION_REQUESTED: 'Modification requested',
};

/** Mirrors backend AuthorizationQuotationRevisionDto exactly. */
export interface AuthorizationQuotationRevision {
  id: string;
  revisionNumber: number;
  status: AuthorizationQuotationRevisionStatus;
  quotationReference: string | null;
  documentAssetId: string | null;
  documentName: string | null;
  /** Staff-only AME signed URL (D-128 Q-4). Never shown to a customer. */
  documentUrl: string | null;
  preparedById: string;
  preparedByName: string;
  preparedAt: string;
  sentCommunicationId: string | null;
  sentAt: string | null;
  decisionCommunicationId: string | null;
  decidedAt: string | null;
  revisionReason: string | null;
  supersededByRevisionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors backend AuthorizationQuotationThreadDto exactly. */
export interface AuthorizationQuotationThread {
  authorizationId: string;
  /** Whether the case's request kind carries a quotation (design §C.2). */
  requiresQuotation: boolean;
  threadStatus: AuthorizationQuotationThreadStatus;
  /** ACCEPTED or REJECTED reached — no further revision (Q-1). */
  closed: boolean;
  currentRevision: AuthorizationQuotationRevision | null;
  /** Ascending by revisionNumber; append-only. */
  revisions: AuthorizationQuotationRevision[];
}

export interface PrepareQuotationRevisionPayload {
  quotationReference?: string;
}

export interface UpdateQuotationRevisionPayload {
  quotationReference?: string | null;
}

export interface SendQuotationRevisionPayload {
  method?: AuthorizationCommunicationMethod;
  customerContactId?: string;
  summary: string;
}

export interface ReviseQuotationRevisionPayload {
  note?: string;
  quotationReference?: string;
}

/** Mirrors the server allowlist (`QUOTATION_DOCUMENT_MIME_TYPES`) for the file picker's
 * `accept` hint only — the server remains the authority on what it accepts. */
export const QUOTATION_DOCUMENT_ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png';

const base = (id: string) => `/authorization-cases/${id}/quotation`;

export const authorizationQuotationService = {
  getThread: (id: string): Promise<AuthorizationQuotationThread> =>
    apiClient.get<AuthorizationQuotationThread>(base(id)).then((r) => r.data),

  /** Revision 1 — the Commercial assignee only. */
  prepare: (
    id: string,
    payload: PrepareQuotationRevisionPayload,
  ): Promise<AuthorizationQuotationThread> =>
    apiClient
      .post<AuthorizationQuotationThread>(`${base(id)}/revisions`, payload)
      .then((r) => r.data),

  /** Edit the DRAFT's reference before it is sent (a sent revision is immutable). */
  updateDraft: (
    id: string,
    revisionId: string,
    payload: UpdateQuotationRevisionPayload,
  ): Promise<AuthorizationQuotationThread> =>
    apiClient
      .patch<AuthorizationQuotationThread>(`${base(id)}/revisions/${revisionId}`, payload)
      .then((r) => r.data),

  /** Attach or replace the document on a DRAFT (multipart `file`, via AME on the server). */
  uploadDocument: (
    id: string,
    revisionId: string,
    file: File,
  ): Promise<AuthorizationQuotationThread> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<AuthorizationQuotationThread>(`${base(id)}/revisions/${revisionId}/document`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /** Send (rev 1) or resend (rev > 1) — the existing customer communication, linked to
   * this revision. Refused by the server when the revision carries no content (Q-2). */
  send: (
    id: string,
    revisionId: string,
    payload: SendQuotationRevisionPayload,
  ): Promise<AuthorizationQuotationThread> =>
    apiClient
      .post<AuthorizationQuotationThread>(`${base(id)}/revisions/${revisionId}/send`, payload)
      .then((r) => r.data),

  /** Open revision n+1 after the customer requested a modification. */
  revise: (
    id: string,
    revisionId: string,
    payload: ReviseQuotationRevisionPayload,
  ): Promise<AuthorizationQuotationThread> =>
    apiClient
      .post<AuthorizationQuotationThread>(`${base(id)}/revisions/${revisionId}/revise`, payload)
      .then((r) => r.data),
};
